import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import { getMessaging, isSupported } from 'firebase/messaging';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Initialize Firebase (prevent re-initialization)
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

// Initialize Auth with Google Provider
export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();

// Configure Google Provider
googleProvider.setCustomParameters({
  prompt: 'select_account', // Always show account selector
});

// IMPORTANT: Initialize Firestore with NAMED database 'nepro'
// Nepro uses a named database, NOT the default database
const databaseId = import.meta.env.VITE_FIREBASE_DATABASE_ID || 'nepro';
export const db = getFirestore(app, databaseId);

// Initialize Storage
export const storage = getStorage(app);

// Initialize Functions (for adminSendMessage, adminSubmitMetaTemplates, adminRunHealthCheck callables)
// IMPORTANT: these callables are deployed to asia-south1, not the us-central1 default —
// getFunctions(app) with no region silently targets us-central1, where they don't exist (404),
// which the callable SDK surfaces to the client as a generic "internal" error.
export const functions = getFunctions(app, 'asia-south1');

// Initialize Messaging (only if supported)
export const initializeMessaging = async () => {
  const supported = await isSupported();
  if (supported) {
    return getMessaging(app);
  }
  console.warn('Firebase Messaging is not supported in this browser');
  return null;
};

// Export app instance
export { app };

// Collection references (for convenience)
export const COLLECTIONS = {
  CUSTOMERS: 'customers',
  SERVICE_PROVIDERS: 'serviceProviders',
  LISTINGS: 'listings',
  BOOKING_INQUIRIES: 'bookingInquiries',
  ORDERS: 'orders',
  CHATS: 'chats',
  CONFIG: 'config',
} as const;

// Config document paths
export const CONFIG_PATHS = {
  SETTINGS: 'config/settings',
  CITY_LIST: 'config/settings/cityList',
  MAINTENANCE: 'config/maintenance',
  NOTICE: 'config/notice',
  ADMIN_EMAILS: 'config/settings/adminEmails',
} as const;
